import React from 'react';
import { useIntl } from 'react-intl';
import type { MessageRequestDetail } from '@api/billing';
import {
  RequestContainer,
  RequestHeader,
  HeaderLeftColumn,
  NodeLabel,
  TimestampText,
  CreditsAmount,
  MetaRow,
  MetaItem,
  MetaSeparator,
  TokenGroup,
  TokenValue,
  TokenLabel,
  DetailsRow,
  ToolChip,
  ToolList,
} from './MessageRequestGroup.styled';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  detail: MessageRequestDetail;
}

const MessageRequestGroup: React.FC<Props> = ({ detail }) => {
  const intl = useIntl();

  return (
    <RequestContainer data-testid="message-request-group">
      <RequestHeader>
        <HeaderLeftColumn>
          {detail.description && (
            <NodeLabel data-testid="message-request-description">
              {detail.description}
            </NodeLabel>
          )}
          <TimestampText>{formatDateTime(detail.timestamp)}</TimestampText>
        </HeaderLeftColumn>
        <CreditsAmount>
          {detail.totalCredits.toLocaleString(undefined, { maximumFractionDigits: 4 })}
          {' '}
          {intl.formatMessage({ id: 'billing.usage.credits', defaultMessage: 'credits' })}
        </CreditsAmount>
      </RequestHeader>

      <MetaRow>
        <MetaItem>
          {intl.formatMessage(
            { id: 'billing.usage.iterations', defaultMessage: '{count, plural, one {# iteration} other {# iterations}}' },
            { count: detail.transactionCount },
          )}
        </MetaItem>
      </MetaRow>

      <DetailsRow>
        <TokenGroup>
          <TokenValue>{detail.totalInputTokens.toLocaleString()}</TokenValue>
          <TokenLabel>
            {intl.formatMessage(
              { id: 'billing.usage.inputTokens', defaultMessage: '{count} input' },
              { count: '' },
            ).trim()}
          </TokenLabel>
          <MetaSeparator aria-hidden>·</MetaSeparator>
          <TokenValue>{detail.totalOutputTokens.toLocaleString()}</TokenValue>
          <TokenLabel>
            {intl.formatMessage(
              { id: 'billing.usage.outputTokens', defaultMessage: '{count} output' },
              { count: '' },
            ).trim()}
          </TokenLabel>
          {detail.totalCacheReadTokens > 0 && (
            <>
              <MetaSeparator aria-hidden>·</MetaSeparator>
              <TokenValue data-testid="cache-read-tokens">
                {detail.totalCacheReadTokens.toLocaleString()}
              </TokenValue>
              <TokenLabel>
                {intl.formatMessage(
                  { id: 'billing.usage.cacheReadTokens', defaultMessage: '{count} cached' },
                  { count: '' },
                ).trim()}
              </TokenLabel>
            </>
          )}
          {detail.totalCacheWriteTokens > 0 && (
            <>
              <MetaSeparator aria-hidden>·</MetaSeparator>
              <TokenValue data-testid="cache-write-tokens">
                {detail.totalCacheWriteTokens.toLocaleString()}
              </TokenValue>
              <TokenLabel>
                {intl.formatMessage(
                  { id: 'billing.usage.cacheWriteTokens', defaultMessage: '{count} cache write' },
                  { count: '' },
                ).trim()}
              </TokenLabel>
            </>
          )}
          <TokenLabel>
            {intl.formatMessage({ id: 'billing.usage.tokensLabel', defaultMessage: 'tokens' })}
          </TokenLabel>
        </TokenGroup>

        {detail.toolNames.length > 0 && (
          <ToolList>
            {detail.toolNames.map((tool) => (
              <ToolChip key={tool}>{tool}</ToolChip>
            ))}
          </ToolList>
        )}
      </DetailsRow>
    </RequestContainer>
  );
};

export default MessageRequestGroup;
